import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import Footer11 from "../shared/Footer11";
import Navbar11 from "../shared/Navbar11";
import Sidebar11 from "../shared/Sidebar11";
import { FaRegFilePdf } from "react-icons/fa6";
import { ContextBoard } from "../../context/ContextBoard";
import config from "../../config";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import * as tf from "@tensorflow/tfjs";
import * as blazeface from "@tensorflow-models/blazeface";
import * as cocoSsd from "@tensorflow-models/coco-ssd";

let faceModel = null;
let objectModel = null;

async function loadModels() {
  if (!faceModel) faceModel = await blazeface.load();
  if (!objectModel) objectModel = await cocoSsd.load();
}

function detectNonHumanTexture(img, face) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  // Extract face region
  const x = Math.max(0, Math.floor(face.topLeft[0]));
  const y = Math.max(0, Math.floor(face.topLeft[1]));
  const w = Math.min(
    img.width - x,
    Math.floor(face.bottomRight[0] - face.topLeft[0])
  );
  const h = Math.min(
    img.height - y,
    Math.floor(face.bottomRight[1] - face.topLeft[1])
  );

  if (w <= 0 || h <= 0) return { isNonHuman: false, reason: null };

  const imageData = ctx.getImageData(x, y, w, h);
  const pixels = imageData.data;

  // Check for skin tone range (broader to include all human skin tones)
  let skinPixels = 0;
  let grayPixels = 0;
  let totalPixels = pixels.length / 4;

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];

    // Human skin tone detection (very broad range)
    const isLikelySkin =
      r > 60 &&
      g > 40 &&
      b > 20 && // Not too dark
      r > b &&
      r > g && // Red channel dominant
      r - g > 10 && // Red-green separation
      Math.abs(r - g) < 100; // Not too extreme

    // Gray/blue tones (common in animals like dogs)
    const isGray =
      Math.abs(r - g) < 20 && Math.abs(g - b) < 20 && Math.abs(r - b) < 20;

    if (isLikelySkin) skinPixels++;
    if (isGray) grayPixels++;
  }

  const skinRatio = skinPixels / totalPixels;
  const grayRatio = grayPixels / totalPixels;

  // If less than 15% skin tones or more than 40% gray, likely not human
  if (skinRatio < 0.15) {
    return {
      isNonHuman: true,
      reason:
        "Skin tone analysis failed - image does not appear to be a human face",
    };
  }

  if (grayRatio > 0.4) {
    return {
      isNonHuman: true,
      reason:
        "Color analysis suggests non-human subject (possible animal fur detected)",
    };
  }

  return { isNonHuman: false, reason: null };
}

/**
 * Detects face coverings like masks, sunglasses, hands, scarves, hats
 */
function detectFaceCoverings(img, face, landmarks) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const [leftEye, rightEye, nose, mouth, leftEar, rightEar] = landmarks;

  // ⚠️ Calculate face dimensions FIRST
  const faceWidth = face.bottomRight[0] - face.topLeft[0];
  const faceHeight = face.bottomRight[1] - face.topLeft[1];
  const checkRadius = Math.min(faceWidth, faceHeight) * 0.08;

  // Extract regions around key facial features
  const extractRegion = (centerX, centerY, radius) => {
    const x = Math.max(0, Math.floor(centerX - radius));
    const y = Math.max(0, Math.floor(centerY - radius));
    const size = Math.floor(radius * 2);
    const w = Math.min(size, img.width - x);
    const h = Math.min(size, img.height - y);

    if (w <= 0 || h <= 0) return null;
    return ctx.getImageData(x, y, w, h);
  };

  // Check if a region has skin tone pixels
  const analyzeSkinTone = (imageData) => {
    if (!imageData) return { skinRatio: 0, darkRatio: 0, brightRatio: 0 };

    const pixels = imageData.data;
    let skinPixels = 0;
    let darkPixels = 0;
    let brightPixels = 0;
    let totalPixels = pixels.length / 4;

    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      const brightness = (r + g + b) / 3;

      // Skin tone detection (broad range for all ethnicities)
      const isSkin =
        r > 60 &&
        g > 40 &&
        b > 20 &&
        r > b &&
        r > g &&
        r - g > 5 &&
        Math.abs(r - g) < 100;

      // Very dark pixels (sunglasses, dark masks)
      if (brightness < 50) darkPixels++;

      // Very bright/white pixels (medical masks, paper)
      if (brightness > 220 && Math.abs(r - g) < 15 && Math.abs(g - b) < 15) {
        brightPixels++;
      }

      if (isSkin) skinPixels++;
    }

    return {
      skinRatio: skinPixels / totalPixels,
      darkRatio: darkPixels / totalPixels,
      brightRatio: brightPixels / totalPixels,
    };
  };

  // Check eye regions for sunglasses or coverings
  const leftEyeData = extractRegion(leftEye[0], leftEye[1], checkRadius);
  const rightEyeData = extractRegion(rightEye[0], rightEye[1], checkRadius);

  const leftEyeAnalysis = analyzeSkinTone(leftEyeData);
  const rightEyeAnalysis = analyzeSkinTone(rightEyeData);

  // If eye regions are too dark (sunglasses) or have too little skin
  if (leftEyeAnalysis.darkRatio > 0.5 || rightEyeAnalysis.darkRatio > 0.5) {
    return {
      isCovered: true,
      reason:
        "Eyes appear to be covered by sunglasses or dark objects. Eyes must be clearly visible without any coverings or obstructions.",
    };
  }

  if (leftEyeAnalysis.skinRatio < 0.2 || rightEyeAnalysis.skinRatio < 0.2) {
    return {
      isCovered: true,
      reason:
        "Eyes appear to be obstructed. Please ensure both eyes are clearly visible without any coverings, glasses with dark lenses, or obstacles.",
    };
  }

  // Check nose region
  const noseData = extractRegion(nose[0], nose[1], checkRadius * 1.2);
  const noseAnalysis = analyzeSkinTone(noseData);

  if (noseAnalysis.skinRatio < 0.25) {
    return {
      isCovered: true,
      reason:
        "Nose appears to be covered or obstructed. Face must be completely uncovered with no masks, hands, or objects covering the nose.",
    };
  }

  if (noseAnalysis.brightRatio > 0.4) {
    return {
      isCovered: true,
      reason:
        "Possible mask or covering detected over nose area. Face must be completely uncovered.",
    };
  }

  // Check mouth region
  const mouthData = extractRegion(mouth[0], mouth[1], checkRadius * 1.3);
  const mouthAnalysis = analyzeSkinTone(mouthData);

  if (mouthAnalysis.skinRatio < 0.2) {
    return {
      isCovered: true,
      reason:
        "Mouth appears to be covered or obstructed. Face must be completely uncovered with no masks, scarves, hands, or objects covering the mouth.",
    };
  }

  if (mouthAnalysis.brightRatio > 0.4) {
    return {
      isCovered: true,
      reason:
        "Possible mask or covering detected over mouth area. Face must be completely uncovered.",
    };
  }

  // Check forehead region (detect hats, headbands, hands)
  const foreheadY = face.topLeft[1] + faceHeight * 0.15;
  const foreheadX = (leftEye[0] + rightEye[0]) / 2;
  const foreheadData = extractRegion(foreheadX, foreheadY, checkRadius * 1.5);
  const foreheadAnalysis = analyzeSkinTone(foreheadData);

  if (foreheadAnalysis.skinRatio < 0.15) {
    return {
      isCovered: true,
      reason:
        "Forehead appears to be covered by hat, headband, hand, or other obstacle. Entire face must be clearly visible without any coverings.",
    };
  }

  // Check entire face region for overall skin visibility
  const faceX = Math.max(0, Math.floor(face.topLeft[0]));
  const faceY = Math.max(0, Math.floor(face.topLeft[1]));
  const faceW = Math.min(
    img.width - faceX,
    Math.floor(face.bottomRight[0] - face.topLeft[0])
  );
  const faceH = Math.min(
    img.height - faceY,
    Math.floor(face.bottomRight[1] - face.topLeft[1])
  );

  if (faceW > 0 && faceH > 0) {
    const fullFaceData = ctx.getImageData(faceX, faceY, faceW, faceH);
    const fullFaceAnalysis = analyzeSkinTone(fullFaceData);

    if (fullFaceAnalysis.skinRatio < 0.25) {
      return {
        isCovered: true,
        reason:
          "Significant portion of face appears to be covered or obstructed. Face must be completely visible without any coverings, hands, objects, or obstacles.",
      };
    }
  }

  return { isCovered: false, reason: null };
}

async function validatePhotograph(img) {
  await loadModels();

  try {
    // ⚠️ CRITICAL: Run object detection FIRST to filter out animals
    const objects = await objectModel.detect(img);

    // Block animals immediately
    const animalClasses = [
      "bird",
      "cat",
      "dog",
      "horse",
      "sheep",
      "cow",
      "elephant",
      "bear",
      "zebra",
      "giraffe",
      "tiger",
      "lion",
      "monkey",
    ];
    const detectedAnimals = objects.filter(
      (obj) => animalClasses.includes(obj.class) && obj.score > 0.25
    );

    if (detectedAnimals.length > 0) {
      throw new Error(
        `Animal detected in image (${detectedAnimals[0].class}). Only photographs of real human faces are allowed.`
      );
    }

    // Require person detection with high confidence
    const personObjects = objects.filter(
      (obj) => obj.class === "person" && obj.score > 0.75
    );

    if (personObjects.length === 0) {
      throw new Error(
        "No person detected in the image. Please upload a clear photograph of a real human being."
      );
    }

    if (personObjects.length > 1) {
      throw new Error(
        "Multiple people detected in the image. Please upload a photograph with only one person."
      );
    }

    // Block non-photo objects
    const nonPhotoClasses = [
      "tv",
      "laptop",
      "cell phone",
      "book",
      "teddy bear",
      "frisbee",
      "clock",
      "monitor",
      "tablet",
    ];
    const nonPhotoObjects = objects.filter(
      (obj) => nonPhotoClasses.includes(obj.class) && obj.score > 0.4
    );

    if (nonPhotoObjects.length > 0) {
      throw new Error(
        "Image appears to contain objects suggesting this is not a direct photograph of a person (screen/device detected). Please upload a real photograph."
      );
    }

    // ⚠️ Detect face coverings and accessories that obstruct the face
    const accessoryClasses = [
      "sunglasses",
      "tie",
      "handbag",
      "backpack",
      "umbrella",
      "bottle",
      "cup",
      "fork",
      "knife",
      "spoon",
      "bowl",
    ];
    const accessories = objects.filter(
      (obj) => accessoryClasses.includes(obj.class) && obj.score > 0.4
    );

    if (accessories.length > 0) {
      throw new Error(
        `Accessories or objects detected near face (${accessories[0].class}). Face must be completely uncovered with no sunglasses, masks, or objects obstructing the view.`
      );
    }

    // Now detect faces with stricter validation
    const faceDetections = await faceModel.estimateFaces(img, false);

    if (faceDetections.length === 0) {
      throw new Error(
        "No human face detected in the image. Please upload a clear passport-size photograph showing your face."
      );
    }

    if (faceDetections.length > 1) {
      throw new Error(
        `Multiple faces detected (${faceDetections.length} faces found). Please upload a photograph with only one person's face.`
      );
    }

    const face = faceDetections[0];

    // ⚠️ STRICTER confidence threshold
    if (face.probability && face.probability < 0.92) {
      throw new Error(
        "Face detection confidence is too low. Please upload a high-quality, well-lit photograph of a real person."
      );
    }

    if (!face.landmarks || face.landmarks.length < 6) {
      throw new Error(
        "Insufficient facial features detected. All features must be clearly visible: 2 eyes, 1 nose, 1 mouth, and 2 ears. Please upload a clear, forward-facing photograph with no obstructions."
      );
    }

    const [leftEye, rightEye, nose, mouth, leftEar, rightEar] = face.landmarks;

    // ✅ CRITICAL: Validate ALL required facial features
    if (!leftEye || !rightEye) {
      throw new Error(
        "Both eyes must be clearly visible in the photograph. Please upload a clear passport-size photo with both eyes showing."
      );
    }

    if (!nose) {
      throw new Error(
        "Nose is not clearly visible in the photograph. Please upload a clear, forward-facing photo showing your nose."
      );
    }

    if (!mouth) {
      throw new Error(
        "Mouth is not clearly visible in the photograph. Please upload a clear photo showing your mouth."
      );
    }

    if (!leftEar || !rightEar) {
      throw new Error(
        "Both ears must be clearly visible in the photograph. Please upload a clear, forward-facing photo with both ears showing (hair should not cover ears)."
      );
    }

    // Calculate face dimensions for subsequent checks
    const faceWidth = face.bottomRight[0] - face.topLeft[0];
    const faceHeight = face.bottomRight[1] - face.topLeft[1];

    // ✅ Validate feature positions (ensure they're within image bounds)
    const validateFeaturePosition = (feature, featureName) => {
      if (
        feature[0] < 0 ||
        feature[0] > img.width ||
        feature[1] < 0 ||
        feature[1] > img.height
      ) {
        throw new Error(
          `${featureName} position is invalid. Please ensure the entire face is visible within the frame.`
        );
      }
    };

    validateFeaturePosition(leftEye, "Left eye");
    validateFeaturePosition(rightEye, "Right eye");
    validateFeaturePosition(nose, "Nose");
    validateFeaturePosition(mouth, "Mouth");
    validateFeaturePosition(leftEar, "Left ear");
    validateFeaturePosition(rightEar, "Right ear");

    // ✅ Validate BOTH ears visibility with pixel analysis
    const validateEarVisibility = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const checkEarRegion = (ear, earName) => {
        // ⚠️ CRITICAL: First check if ear is actually within reasonable bounds
        // Ears should be outside the eye region but within face boundaries
        const faceLeft = face.topLeft[0];
        const faceRight = face.bottomRight[0];
        const faceTop = face.topLeft[1];
        const faceBottom = face.bottomRight[1];

        // Ear must be within extended face region (add 10% margin)
        const margin = faceWidth * 0.1;
        if (
          ear[0] < faceLeft - margin ||
          ear[0] > faceRight + margin ||
          ear[1] < faceTop - margin ||
          ear[1] > faceBottom + margin
        ) {
          throw new Error(
            `${earName} is outside the visible face region. Both ears must be clearly visible within the photograph frame.`
          );
        }

        const earRadius = Math.min(faceWidth, faceHeight) * 0.08;
        const x = Math.max(0, Math.floor(ear[0] - earRadius));
        const y = Math.max(0, Math.floor(ear[1] - earRadius));
        const size = Math.floor(earRadius * 2);
        const w = Math.min(size, img.width - x);
        const h = Math.min(size, img.height - y);

        if (w <= 0 || h <= 0 || w < size * 0.7 || h < size * 0.7) {
          throw new Error(
            `${earName} is not fully within image bounds. Please ensure the entire face including both ears is visible in the frame.`
          );
        }

        const earData = ctx.getImageData(x, y, w, h);
        const pixels = earData.data;
        let skinPixels = 0;
        let handColorPixels = 0;
        let totalPixels = pixels.length / 4;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];

          // Skin tone detection (ear skin)
          const isSkin =
            r > 60 &&
            g > 40 &&
            b > 20 &&
            r > b &&
            r > g &&
            r - g > 5 &&
            Math.abs(r - g) < 100;

          // Detect hand/finger covering (similar to skin but different texture)
          // Hands blocking face typically show very high skin tone ratio but wrong location
          const isHandColor =
            r > 80 && g > 50 && b > 30 && r > b && Math.abs(r - g) < 80;

          if (isSkin) skinPixels++;
          if (isHandColor) handColorPixels++;
        }

        const skinRatio = skinPixels / totalPixels;
        const handRatio = handColorPixels / totalPixels;

        // ⚠️ STRICTER: Ears should have 40%+ skin visibility (increased from 30%)
        if (skinRatio < 0.4) {
          throw new Error(
            `${earName} is not clearly visible (may be covered by hair, hands, clothing, or outside frame). Both ears must be fully visible and uncovered in the photograph. Please ensure ears are not hidden and retake the photo.`
          );
        }

        // ⚠️ NEW: If region has high hand-color but ear is supposedly there, likely hand blocking
        if (handRatio > 0.7 && skinRatio < 0.5) {
          throw new Error(
            `${earName} appears to be blocked by hand or fingers. Please remove hands from face and ensure both ears are clearly visible.`
          );
        }

        // ⚠️ NEW: Check for edge cases - ear too close to image border
        const borderThreshold = Math.min(img.width, img.height) * 0.05;
        if (
          ear[0] < borderThreshold ||
          ear[0] > img.width - borderThreshold ||
          ear[1] < borderThreshold ||
          ear[1] > img.height - borderThreshold
        ) {
          throw new Error(
            `${earName} is too close to the image edge. Please ensure the entire face including both ears is well within the frame with adequate margins.`
          );
        }
      };

      // Check BOTH ears
      checkEarRegion(leftEar, "Left ear");
      checkEarRegion(rightEar, "Right ear");
    };

    validateEarVisibility();

    // ⚠️ Texture analysis to detect fur/non-human patterns
    const textureCheck = detectNonHumanTexture(img, face);
    if (textureCheck.isNonHuman) {
      throw new Error(textureCheck.reason);
    }

    // ⚠️ Check for face coverings (masks, sunglasses, hands, etc.)
    const coveringCheck = detectFaceCoverings(img, face, face.landmarks);
    if (coveringCheck.isCovered) {
      throw new Error(coveringCheck.reason);
    }

    // ⚠️ Validate ear-eye alignment (ears should be roughly at eye level)
    const earEyeAlignment = () => {
      const eyeAvgY = (leftEye[1] + rightEye[1]) / 2;
      const leftEarDiff = Math.abs(leftEar[1] - eyeAvgY);
      const rightEarDiff = Math.abs(rightEar[1] - eyeAvgY);

      // Ears should be within 30% of face height from eye level
      const maxEarDeviation = faceHeight * 0.3;

      if (leftEarDiff > maxEarDeviation || rightEarDiff > maxEarDeviation) {
        throw new Error(
          "Facial structure is abnormal. Ears must be at approximately eye level for a valid human face photograph."
        );
      }
    };

    earEyeAlignment();

    // Calculate head tilt angle (degrees)
    const dx = rightEye[0] - leftEye[0];
    const dy = rightEye[1] - leftEye[1];
    const angleInDegrees = Math.atan2(dy, dx) * (180 / Math.PI);

    if (Math.abs(angleInDegrees) > 15) {
      throw new Error(
        `Head is tilted or rotated (${angleInDegrees.toFixed(
          1
        )}°). Please upload a straight, forward-facing photo.`
      );
    }

    // Reject upside-down faces (mouth above eyes)
    const eyesY = (leftEye[1] + rightEye[1]) / 2;
    if (mouth[1] < eyesY) {
      throw new Error(
        "Face appears inverted (upside down). Please rotate your photo upright."
      );
    }

    // ⚠️ STRICTER eye distance ratio check
    const eyeDistance = Math.sqrt(
      Math.pow(rightEye[0] - leftEye[0], 2) +
        Math.pow(rightEye[1] - leftEye[1], 2)
    );
    const eyeDistanceRatio = eyeDistance / faceWidth;

    if (eyeDistanceRatio < 0.25 || eyeDistanceRatio > 0.55) {
      throw new Error(
        "Facial proportions do not match human characteristics. Please ensure this is a photograph of a real person, not an animal or artificial image."
      );
    }

    // ⚠️ STRICTER nose position check
    const eyeCenter = [
      (leftEye[0] + rightEye[0]) / 2,
      (leftEye[1] + rightEye[1]) / 2,
    ];
    const noseToEyeDistance = Math.abs(nose[1] - eyeCenter[1]);
    const noseRatio = noseToEyeDistance / faceHeight;

    if (noseRatio < 0.12 || noseRatio > 0.35) {
      throw new Error(
        "Facial structure does not appear to be human. Please upload a photograph of a real person, not a drawing, animal, or artificial image."
      );
    }

    // ⚠️ Mouth position validation
    const mouthToEyeDistance = mouth[1] - eyeCenter[1];
    const mouthRatio = mouthToEyeDistance / faceHeight;

    if (mouthRatio < 0.15 || mouthRatio > 0.5) {
      throw new Error(
        "Facial proportions are abnormal. Please ensure this is a real human face, not an animal or manipulated image."
      );
    }

    // Face size relative to image check
    if (face.topLeft && face.bottomRight) {
      const faceBoxWidth = face.bottomRight[0] - face.topLeft[0];
      const faceBoxHeight = face.bottomRight[1] - face.topLeft[1];
      const faceArea = faceBoxWidth * faceBoxHeight;
      const imageArea = img.width * img.height;
      const faceRatio = faceArea / imageArea;

      if (faceRatio < 0.15) {
        throw new Error(
          "Face is too small in the image. Please upload a passport-size photograph where your face takes up most of the frame."
        );
      }
      if (faceRatio > 0.75) {
        throw new Error(
          "Face is too close to the camera. Please maintain appropriate distance for a passport-size photograph."
        );
      }

      // ✅ NEW: Check for face not near image edge (prevent cropped faces)
      const x = face.topLeft[0];
      const y = face.topLeft[1];
      const x2 = face.bottomRight[0];
      const y2 = face.bottomRight[1];
      const margin = 20; // pixels from edge

      if (
        x < margin ||
        y < margin ||
        x2 > img.width - margin ||
        y2 > img.height - margin
      ) {
        throw new Error(
          "Face appears cropped or too close to the edge. Ensure the full face is centered and visible with adequate margins from all edges."
        );
      }
    }

    // Image orientation checks
    if (img.width > img.height) {
      throw new Error(
        "Image must be in portrait orientation (height should be greater than width)."
      );
    }

    const aspectRatio = img.height / img.width;
    if (aspectRatio < 1.2 || aspectRatio > 1.6) {
      throw new Error(
        "Image aspect ratio is not suitable for passport-size photograph. Please use a 3:4 or similar portrait ratio."
      );
    }

    return true;
  } catch (error) {
    throw error;
  }
}
// Additional function to validate image realism
async function validateImageRealism(img) {
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Check for unnatural color distributions that might indicate drawings/cartoons
  let colorVariance = 0;
  let totalPixels = 0;
  const colorCounts = {};

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const colorKey = `${Math.floor(r / 32)}_${Math.floor(g / 32)}_${Math.floor(
      b / 32
    )}`;

    colorCounts[colorKey] = (colorCounts[colorKey] || 0) + 1;
    totalPixels++;
  }

  const uniqueColors = Object.keys(colorCounts).length;
  const colorDiversity = uniqueColors / totalPixels;

  // Real photographs typically have high color diversity
  if (colorDiversity > 0.1) {
    throw new Error(
      "Image appears to be artificially generated or heavily processed. Please upload a natural photograph of yourself."
    );
  }

  // Check for extremely uniform regions that might indicate digital art
  const maxColorCount = Math.max(...Object.values(colorCounts));
  const dominantColorRatio = maxColorCount / totalPixels;

  if (dominantColorRatio > 0.4) {
    throw new Error(
      "Image has unnatural color distribution. Please ensure this is a real photograph, not a drawing or digital artwork."
    );
  }
}
// Validate Signature → must have no faces
async function validateSignature(img) {
  await loadModels();
  const predictions = await faceModel.estimateFaces(img, false);
  if (predictions.length > 0) {
    throw new Error("❌ Signature must not contain a face/photo.");
  }
  const objects = await objectModel.detect(img);
  if (objects.some((o) => o.class === "person" && o.score > 0.7)) {
    throw new Error("❌ Signature must be handwriting only, not a photo.");
  }
  return true;
}

function convertToGrayscale(img) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const avg = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      data[i] = data[i + 1] = data[i + 2] = avg;
    }
    ctx.putImageData(imageData, 0, 0);
    const grayDataUrl = canvas.toDataURL("image/jpeg");
    const grayImg = new Image();
    grayImg.src = grayDataUrl;
    grayImg.onload = () => resolve(grayImg);
  });
}
function showLoadingProgress() {
  Swal.fire({
    title: "Loading AI models...",
    html: `
      <div style="width:100%; background:#eee; border-radius:8px; overflow:hidden;">
        <div id="progress-bar" style="height:20px; width:0%; background:#4caf50; text-align:center; color:white;">
          0%
        </div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });
}

async function loadModelsWithFakeProgress() {
  showLoadingProgress();

  let progress = 0;
  const bar = document.getElementById("progress-bar");

  // Fake progress function
  const fakeProgress = () =>
    new Promise((resolve) => {
      const interval = setInterval(() => {
        progress += Math.floor(Math.random() * 3) + 1; // increment 1-3%
        if (progress > 95) progress = 95; // stop at 95% until models load
        if (bar) {
          bar.style.width = `${progress}%`;
          bar.innerText = `${progress}%`;
        }
      }, 50);
      resolve(interval);
    });

  const interval = await fakeProgress();

  // Load face model
  if (!faceModel) await blazeface.load().then((m) => (faceModel = m));

  // Load object model
  if (!objectModel) await cocoSsd.load().then((m) => (objectModel = m));

  // Once models loaded, finish progress smoothly to 100%
  clearInterval(interval);
  for (let i = progress; i <= 100; i++) {
    if (bar) {
      bar.style.width = `${i}%`;
      bar.innerText = `${i}%`;
    }
    await new Promise((r) => setTimeout(r, 10)); // smooth increment
  }

  Swal.close();
}

function Document11() {
  const navigate = useNavigate();
  const {
    class11exregularmode,
    setClass11exregularmode,
    setClass11Mode,
    class11othermode,
    setClass11OtherMode,
    class11before2010mode,
    setClass11before2010mode,
    class11mode,
    class11candidateid,
    class11castecode,
    class11divyangcode,
    setClass11Candidateid,
    setClass11Castecode,
    setClass11Divyangcode,
  } = useContext(ContextBoard);

  const location = useLocation();
  const [data, setData] = useState({});
  const { candidateId, referenceNo } = location.state || {};
  const photographRef = useRef();
  const signatureRef = useRef();
  const casteRef = useRef();
  const divyangRef = useRef();

  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [signFile, setSignFile] = useState(null);
  const [casteFile, setCasteFile] = useState(null);
  const [divyangFile, setDivyangFile] = useState(null);

  const [photoBase64, setPhotoBase64] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [photourl, setPhotourl] = useState("");

  const [signBase64, setSignBase64] = useState("");
  const [signName, setSignName] = useState("");
  const [signurl, setSignurl] = useState("");

  const [casteBase64, setCasteBase64] = useState("");
  const [casteName, setCasteName] = useState("");
  const [casteurl, setCasteurl] = useState("");

  const [divyangBase64, setDivyangBase64] = useState("");
  const [divyangName, setDivyangName] = useState("");
  const [divyangurl, setDivyangurl] = useState("");

  const [aistatus, setAistatus] = useState("");
  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState("");
  const cropperRef = useRef(null);

  // useEffect(() => {
  //   const initModels = async () => {
  //     await loadModelsWithFakeProgress();
  //   };
  //   initModels();
  // }, []);
  useEffect(() => {
    const fetchCandidateData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("tokenclassxi");

        const response = await fetch(
          `${config.API_BASE_URL}/StudentDataByCandidate_CL11`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              CANDIDATE_ID: class11candidateid,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const result = await response.json();
        setAistatus(result.data.studentData_list.aI_ON_OFF_CL11 || "");
        if (result.data.studentData_list.aI_ON_OFF_CL11 === "ON") {
          await loadModelsWithFakeProgress();
        }
        setData(result.data?.studentData_list);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching candidate data:", error);
      } finally {
        setLoading(false);
      }
    };
    console.log(referenceNo, candidateId);
    fetchCandidateData();
  }, []);

  useEffect(() => {
    // Redirect if no candidateid
    if (!class11candidateid) {
      navigate("/class-xi-dashboard");
      return;
    }
    // If mode is edit, call API
    if (
      class11mode === "edit" ||
      class11othermode === "edit" ||
      class11exregularmode === "edit" ||
      class11before2010mode === "edit"
    ) {
      const fetchEditData = async () => {
        setLoading(true);
        try {
          const token = sessionStorage.getItem("tokenclassxi");
          const response = await fetch(
            `${config.API_BASE_URL}/FetchStudentUploadedDoc_CL11`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                candidatE_ID: class11candidateid,
                districtcode: sessionStorage.getItem("district") || "",
              }),
            }
          );

          const data = await response.json();
          if (response.ok) {
            setLoading(false);
            // set state with the response data
            console.log("Edit data:", data);
            const maindata = data.data.studentDocument_list;
            setPhotoName(maindata.studenT_PHOTOGRAPH_NAME || "");
            setSignName(maindata.studenT_SIGNATURE_NAME || "");
            setCasteName(maindata.castE_CERTIFICATE_NAME || "");
            setDivyangName(maindata.divyanG_CATEGORY_NAME || "");
            setPhotourl(maindata.studenT_PHOTOGRAPH_URL || "");
            setSignurl(maindata.studenT_SIGNATURE_URL || "");
          } else {
            setLoading(false);
            console.error("API error:", data.message);
          }
        } catch (error) {
          setLoading(false);
          console.error("Fetch error:", error);
        }
      };

      fetchEditData();
    }
  }, [class11candidateid, class11mode, navigate]);

  const validateFile = (file, type) => {
    const validTypes =
      type === "image" ? ["image/jpeg", "image/jpg"] : ["application/pdf"];
    const minSize = type === "image" ? 10 * 1024 : 30 * 1024; // 10KB / 30KB
    const maxSize = type === "image" ? 40 * 1024 : 300 * 1024; // 40KB / 300KB

    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type.");
      return false;
    }

    if (file.size < minSize || file.size > maxSize) {
      toast.error("File size out of range.");
      return false;
    }

    return true;
  };

  // Crop confirm
  const handleCropConfirm = async () => {
    if (!cropperRef.current) return;

    const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas({
      width: 300,
      height: 400,
    });

    // Convert cropped canvas to base64
    const croppedDataUrl = croppedCanvas.toDataURL("image/jpeg");
    const img = new Image();
    img.src = croppedDataUrl;

    img.onload = async () => {
      try {
        // Step 1: validate original cropped image
        if (aistatus === "ON") {
          await validatePhotograph(img);
        }

        // Step 2: convert AFTER validation
        const grayImg = await convertToGrayscale(img);

        // Step 3: Save grayscale base64
        setPhotoBase64(grayImg.src);

        // Step 4: Save file name
        setPhotoName(photoFile?.name || "cropped-photo.jpg");

        // 🔹 Step 5: Convert cropped canvas into File object and set it
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            const croppedFile = new File(
              [blob],
              photoFile?.name || "cropped-photo.jpg",
              {
                type: "image/jpeg",
              }
            );
            setPhotoFile(croppedFile);
          }
        }, "image/jpeg");

        setShowCropper(false);
      } catch (err) {
        toast.error(err.message);
      }
    };
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!validateFile(file, "image")) {
      if (photographRef.current) photographRef.current.value = "";
      return;
    }

    setPhotoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setCropperImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = ["photograph", "signature"].includes(type)
      ? "image"
      : "pdf";
    const isValid = validateFile(file, fileType);

    if (!isValid) {
      // Reset the input using the correct ref
      if (type === "photograph" && photographRef.current)
        photographRef.current.value = "";
      if (type === "signature" && signatureRef.current)
        signatureRef.current.value = "";
      if (type === "caste" && casteRef.current) casteRef.current.value = "";
      if (type === "divyang" && divyangRef.current)
        divyangRef.current.value = "";
      return;
    }

    // Proceed to convert and preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (type === "photograph") {
        setPhotoBase64(base64);
        setPhotoName(file.name);
      } else if (type === "signature") {
        setSignBase64(base64);
        setSignName(file.name);
      } else if (type === "caste") {
        setCasteBase64(base64);
        setCasteName(file.name);
      } else if (type === "divyang") {
        setDivyangBase64(base64);
        setDivyangName(file.name);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = ["photograph", "signature"].includes(type)
      ? "image"
      : "pdf";
    const isValid = validateFile(file, fileType);

    if (!isValid) {
      // Reset the input using the correct ref
      if (type === "photograph" && photographRef.current)
        photographRef.current.value = "";
      if (type === "signature" && signatureRef.current)
        signatureRef.current.value = "";
      if (type === "caste" && casteRef.current) casteRef.current.value = "";
      if (type === "divyang" && divyangRef.current)
        divyangRef.current.value = "";
      return;
    }

    // Set the file state for change detection!
    if (type === "photograph") {
      setPhotoFile(file);
    } else if (type === "signature") {
      setSignFile(file);
    } else if (type === "caste") {
      setCasteFile(file);
    } else if (type === "divyang") {
      setDivyangFile(file);
    }

    // Preview/convert
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (type === "photograph") {
        setPhotoBase64(base64);
        setPhotoName(file.name);
        // open cropper
        setCropperImage(base64);
        setShowCropper(true);
      } else if (type === "signature") {
        setSignBase64(base64);
        setSignName(file.name);
      } else if (type === "caste") {
        setCasteBase64(base64);
        setCasteName(file.name);
      } else if (type === "divyang") {
        setDivyangBase64(base64);
        setDivyangName(file.name);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCancel = (type) => {
    switch (type) {
      case "photograph":
        setPhotoFile(null);
        setPhotoBase64("");
        setPhotourl("");
        setPhotoName("");
        break;
      case "signature":
        setSignFile(null);
        setSignBase64("");
        setSignurl("");
        setSignName("");
        break;
      case "caste":
        setCasteFile(null);
        setCasteBase64("");
        setCasteurl("");
        setCasteName("");
        break;
      case "divyang":
        setDivyangFile(null);
        setDivyangBase64("");
        setDivyangName("");
        setDivyangurl("");
        break;
      default:
        break;
    }
    // Clear file input
    const fileInput = document.getElementById(`upload_${type}`);
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmit = () => {
    if (!photoName) return toast.error("Photograph is required.");
    if (!signName) return toast.error("Signature is required.");
    if (class11castecode !== "CS_001" && !casteName)
      return toast.error("Caste certificate is required.");
    // if (divyangcode !== "DIV_001" && !divyangName)
    //   return toast.error("Divyang certificate is required.");

    const formData = new FormData();
    formData.append("CANDIDATE_ID", class11candidateid);
    formData.append("districtcode", sessionStorage.getItem("district") || "");
    formData.append("CASTE_CATEGORY", class11castecode || "CS_001");
    formData.append("DIVYANG_CATEGORY", class11divyangcode || "DIV_001");

    if (photoName)
      formData.append("STUDENT_PHOTOGRAPH_FILE", photoFile || null);
    if (signName) formData.append("STUDENT_SIGNATURE_FILE", signFile || null);
    if (casteName && class11castecode !== "CS_001")
      formData.append("CASTE_CERTIFICATE_FILE", casteFile);
    else formData.append("CASTE_CERTIFICATE_FILE", null);

    if (divyangName && class11divyangcode !== "DIV_001")
      formData.append("DIVYANG_CERTIFICATE_FILE", divyangFile);
    else formData.append("DIVYANG_CERTIFICATE_FILE", null);

    formData.append("PHOTOGRAPH_Change", photoFile ? "YES" : "NO");
    formData.append("SIGNATURE_Change", signFile ? "YES" : "NO");
    formData.append("CASTE_Change", casteFile ? "YES" : "NO");
    formData.append("DIVYANG_Change", divyangFile ? "YES" : "NO");

    formData.append("PHOTOGRAPH_name", photoName || "");
    formData.append("SIGNATURE_name", signName || "");
    formData.append("DIVYANG_name", divyangName || "");
    formData.append("CASTE_name", casteName || "");
    console.log(formData);
    const token = sessionStorage.getItem("tokenclassxi");
    setLoading(true);

    fetch(`${config.API_BASE_URL}/InsertStudentUploadDoc_CL11`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })
      .then(async (res) => {
        const responseData = await res.json();

        if (res.status === 200) {
          if (candidateId && referenceNo) {
            const payload = {
              candidatE_CODE: candidateId,
              schooL_CODE: sessionStorage.getItem("schoolCode"),
            };

            const secondRes = await fetch(
              `${config.API_BASE_URL}/AdminUpdateExRegularStudentCorrection_CL11`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
              }
            );

            const secondData = await secondRes.json();

            if (secondRes.status !== 200) {
              Swal.fire(
                "Error",
                secondData?.message ||
                  "Document uploaded but correction update failed",
                "error"
              );
              setLoading(false);
              return;
            }
          }

          Swal.fire(
            "Success",
            class11mode === "edit" ||
              class11othermode === "edit" ||
              class11exregularmode === "edit" ||
              class11before2010mode === "edit"
              ? "Documents updated successfully"
              : "Documents uploaded successfully",
            "success"
          ).then(() => {
            const isEdit =
              class11mode === "edit" ||
              class11othermode === "edit" ||
              class11exregularmode === "edit" ||
              class11before2010mode === "edit";
            navigate("/class-xi-total-registration");
          });
        } else {
          Swal.fire("Error", responseData?.message || "Upload failed", "error");
        }
      })
      .catch((err) => {
        console.error(err);
        Swal.fire("Error", "Network error or server issue", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleCropCancel = () => {
    setShowCropper(false); // close cropper modal
    setPhotoFile(null);
    setPhotoBase64("");
    // setPhotoName("");
    setCropperImage(""); // reset cropper image

    // Reset file input so user can select again
    if (photographRef.current) {
      photographRef.current.value = "";
    }
    setTimeout(() => {
      setPhotoFile(null);
      setCropperImage("");
    }, 0);
  };
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <div class="container-scroller">
        <Navbar11 />
        <div class="container-fluid page-body-wrapper">
          <Sidebar11 />
          <div class="main-panel">
            <div class="content-wrapper">
              <div class="row">
                <div class="col-md-12 mb-1">
                  <section class="content-header">
                    {/* <h1>
                      <i class="fa fa-user-plus"></i>&nbsp; Add New Student
                      (Student Registration Page for Board Examination)
                    </h1> */}
                    <ol class="breadcrumb">
                      <li>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("/class-xi-dashboard");
                          }}
                        >
                          <svg
                            stroke="currentColor"
                            fill="currentColor"
                            stroke-width="0"
                            viewBox="0 0 512 512"
                            height="1em"
                            width="1em"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ marginTop: "-4px" }}
                          >
                            <path d="M256 64C132.3 64 32 164.2 32 287.9c0 57 21.3 109 56.3 148.5 1.1 1.2 2.1 2.4 3.2 3.5C96.3 445 103 448 110 448c7.1 0 13.8-3 18.6-8.2 31.6-34.4 77-55.9 127.4-55.9s95.8 21.6 127.4 55.9c4.8 5.2 11.5 8.2 18.6 8.2 7 0 13.7-2.9 18.5-8.1 1.1-1.2 2.1-2.3 3.2-3.5 35-39.5 56.3-91.5 56.3-148.5C480 164.2 379.7 64 256 64zm-14 45.9c0-7.7 6.3-14 14-14s14 6.3 14 14v36c0 7.7-6.3 14-14 14s-14-6.3-14-14v-36zm-128 192H78c-7.7 0-14-6.3-14-14s6.3-14 14-14h36c7.7 0 14 6.3 14 14s-6.3 14-14 14zm51.5-104.5c-5.4 5.4-14.4 5.4-19.8 0L120.2 172c-5.4-5.4-5.4-14.4 0-19.8s14.4-5.4 19.8 0l25.5 25.5c5.4 5.4 5.4 14.3 0 19.7zm160.6 34.5l-47.5 75.5c-1.9 2.6-4.3 5.1-7 7-13.5 9.7-32.3 6.5-42-7s-6.5-32.3 7-42l75.5-47.5c3.4-2.4 8.1-2.5 11.7 0 4.5 3.2 5.5 9.5 2.3 14zm40.2-34.5c-5.4 5.4-14.4 5.4-19.8 0s-5.4-14.4 0-19.8l25.5-25.5c5.4-5.4 14.4-5.4 19.8 0s5.4 14.4 0 19.8l-25.5 25.5zM434 301.9h-36c-7.7 0-14-6.3-14-14s6.3-14 14-14h36c7.7 0 14 6.3 14 14s-6.3 14-14 14z"></path>
                          </svg>{" "}
                          Home&nbsp;
                        </a>
                      </li>
                      <li class="active"> Add Student</li>
                    </ol>
                    <h3></h3>
                  </section>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12 mb-4 stretch-card">
                  <div class="card">
                    <div class="card-header card-green">
                      <h4 class="mb-0 p-2" style={{ lineHeight: "25px" }}>
                        Document Upload Section.
                      </h4>
                    </div>
                    <div class="card-body">
                      <div className="row mb-3">
                        {data.candidatE_NAME ? (
                          <div className="col-md-4 mb-2">
                            <b>Candidate's Name :</b>{" "}
                            {data?.candidatE_NAME || ""}
                          </div>
                        ) : (
                          ""
                        )}
                        {data.fatheR_NAME ? (
                          <div className="col-md-4 mb-2">
                            <b>Father's Name :</b> {data.fatheR_NAME || ""}
                          </div>
                        ) : (
                          ""
                        )}
                        {data.motheR_NAME ? (
                          <div className="col-md-4 mb-2">
                            <b>Mother's Name :</b> {data.motheR_NAME || ""}
                          </div>
                        ) : (
                          ""
                        )}
                        {data.dob ? (
                          <div className="col-md-4 mb-2">
                            <b>Date of Birth :</b> {data.dob || ""}
                          </div>
                        ) : (
                          ""
                        )}
                        {data.castE_NAME ? (
                          <div className="col-md-4 mb-2">
                            <b>Caste Name :</b> {data.castE_NAME || ""}
                          </div>
                        ) : (
                          ""
                        )}
                        {data.divyanG_NAME ? (
                          <div className="col-md-4 mb-2">
                            <b>Divyang Category :</b> {data.divyanG_NAME || ""}
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                      <div class="table-responsive">
                        <table class="table table-condensed table-bordered">
                          <thead>
                            <tr>
                              <th colspan="4" class="card-green text-white">
                                You Must Upload Photograph And Signature
                              </th>
                            </tr>
                            <tr class="table-primary">
                              <th>Attachment Type</th>
                              <th style={{ width: "20%" }}>File Type</th>
                              <th>Upload File</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                Photograph{" "}
                                <span className="text-reddit">*</span>
                              </td>
                              <td style={{ lineHeight: "17px" }}>
                                <b>Document Format :</b> JPG,JPEG
                                <br />
                                <b>Min Size :</b> 10 KB
                                <br />
                                <b>Max Size :</b> 40 KB
                              </td>
                              <td
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {/* <div className="input-group"> */}
                                {/* <div
                                           class="input-group-prepend"
                                           style={{ width: "90%" }}
                                         > */}
                                <div
                                  style={{ width: "92%" }}
                                  className="file-upload"
                                  data-toggle="tooltip"
                                  data-placement="top"
                                  data-bs-original-title="Document Format : JPG<br>Min Size : 10 KB<br>Max Size : 40KB"
                                >
                                  <div className="file-select">
                                    <div
                                      className="imagePreview"
                                      id="photograph"
                                    >
                                      {(photoBase64 || photourl) && (
                                        <img
                                          src={photoBase64 || photourl}
                                          alt="Preview"
                                          style={{
                                            width: "38px",
                                            height: "38px",
                                            objectFit: "cover",
                                            borderRadius: "6px",
                                          }}
                                        />
                                      )}
                                    </div>
                                    <button className="btn btn-secondary btn-sm file_btn">
                                      Choose File
                                    </button>
                                    <div
                                      className="file-select-name"
                                      id="photograph_name"
                                    >
                                      {photoName
                                        ? photoName.length > 60
                                          ? `${photoName.slice(0, 60)}...`
                                          : photoName
                                        : "No File Chosen"}
                                    </div>

                                    <input
                                      type="file"
                                      ref={photographRef}
                                      className="profileimg form-control"
                                      id="upload_photograph"
                                      accept=".JPG,.JPEG"
                                      // onChange={(e) =>
                                      //   handleChange(e, "photograph")
                                      // }
                                      onChange={(e) => handlePhotoChange(e)}
                                      style={{
                                        position: "absolute",
                                        opacity: 0,
                                        left: 0,
                                        top: 0,
                                      }}
                                    />
                                  </div>
                                </div>
                                {/* </div> */}
                                {/* <div class="input-group-append"> */}
                                <button
                                  className="btn btn-secondary remove-btn"
                                  style={{ padding: "8px 12px" }}
                                  type="button"
                                  onClick={() => handleCancel("photograph")}
                                >
                                  <i className="fa fa-refresh"></i>
                                </button>
                                {/* </div>
                                       </div> */}
                              </td>
                            </tr>
                            <tr>
                              <td>
                                Signature <span className="text-reddit">*</span>
                              </td>
                              <td style={{ lineHeight: "17px" }}>
                                <b>Document Format :</b> JPG,JPEG
                                <br />
                                <b>Min Size :</b> 10 KB
                                <br />
                                <b>Max Size :</b> 40 KB
                              </td>
                              <td
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {/* <div className="input-group">
                                         <div
                                           class="input-group-prepend"
                                           style={{ width: "90%" }}
                                         > */}
                                <div
                                  style={{ width: "92%" }}
                                  className="file-upload"
                                  data-toggle="tooltip"
                                  data-placement="top"
                                  data-bs-original-title="Document Format : JPG<br>Min Size : 10 KB<br>Max Size : 40KB"
                                >
                                  <div className="file-select">
                                    <div
                                      className="imagePreview"
                                      id="signature"
                                    >
                                      {(signBase64 || signurl) && (
                                        <img
                                          src={signBase64 || signurl}
                                          alt="Signature"
                                          style={{
                                            width: "38px",
                                            height: "38px",
                                            objectFit: "cover",
                                            borderRadius: "6px",
                                          }}
                                        />
                                      )}
                                    </div>
                                    <button className="btn btn-secondary btn-sm file_btn">
                                      Choose File
                                    </button>
                                    <div
                                      className="file-select-name"
                                      id="signature_name"
                                    >
                                      {signName
                                        ? signName.length > 60
                                          ? `${signName.slice(0, 60)}...`
                                          : signName
                                        : "No File Chosen"}
                                    </div>

                                    <input
                                      ref={signatureRef}
                                      type="file"
                                      className="profileimg form-control"
                                      id="upload_signature"
                                      accept=".JPG,.JPEG"
                                      onChange={(e) =>
                                        handleChange(e, "signature")
                                      }
                                      style={{
                                        position: "absolute",
                                        opacity: 0,
                                        left: 0,
                                        top: 0,
                                      }}
                                    />
                                  </div>
                                </div>
                                {/* </div>
                                         <div class="input-group-prepend"> */}
                                <button
                                  type="button"
                                  className="btn btn-secondary remove-btn"
                                  style={{ padding: "8px 12px" }}
                                  onClick={() => handleCancel("signature")}
                                >
                                  <i className="fa fa-refresh"></i>
                                </button>
                                {/* </div>
                                       </div> */}
                              </td>
                            </tr>
                            {class11castecode &&
                              class11castecode !== "CS_001" && (
                                <tr>
                                  <td>
                                    Caste Certificate{" "}
                                    <span className="text-reddit">*</span>
                                  </td>
                                  <td style={{ lineHeight: "17px" }}>
                                    <b>Document Format :</b> PDF
                                    <br />
                                    <b>Min Size :</b> 30 KB
                                    <br />
                                    <b>Max Size :</b> 300 KB
                                  </td>
                                  <td
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {/* <div className="input-group">
                                           <div
                                             className="input-group-prepend"
                                             style={{ width: "90%" }}
                                           > */}
                                    <div
                                      style={{ width: "92%" }}
                                      className="file-upload"
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      data-bs-original-title="Document Format : PDF<br>Min Size : 30 KB<br>Max Size : 300KB"
                                    >
                                      <div className="file-select">
                                        <div
                                          className="imagePreview"
                                          id="caste"
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {casteName && (
                                            <a
                                              href={casteurl || casteBase64}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              <FaRegFilePdf
                                                style={{ fontSize: "28px" }}
                                                className="text-danger"
                                              />
                                            </a>
                                          )}
                                        </div>
                                        <button className="btn btn-secondary btn-sm file_btn">
                                          Choose File
                                        </button>

                                        <div
                                          className="file-select-name"
                                          id="caste_name"
                                        >
                                          {casteName
                                            ? casteName.length > 60
                                              ? `${casteName.slice(0, 60)}...`
                                              : casteName
                                            : "No File Chosen"}
                                        </div>
                                        <input
                                          ref={casteRef}
                                          type="file"
                                          className="profileimg form-control"
                                          id="upload_caste"
                                          accept=".PDF"
                                          onChange={(e) =>
                                            handleChange(e, "caste")
                                          }
                                          style={{
                                            position: "absolute",
                                            opacity: 0,
                                            left: 0,
                                            top: 0,
                                          }}
                                        />
                                      </div>
                                    </div>
                                    {/* </div>
                                           <div className="input-group-prepend"> */}
                                    <button
                                      type="button"
                                      className="btn btn-secondary remove-btn"
                                      style={{ padding: "8px 12px" }}
                                      onClick={() => handleCancel("caste")}
                                    >
                                      <i className="fa fa-refresh"></i>
                                    </button>
                                    {/* </div>
                                         </div> */}
                                  </td>
                                </tr>
                              )}
                            {class11divyangcode &&
                              class11divyangcode !== "DIV_001" && (
                                <tr>
                                  <td>Divyang Certificate </td>
                                  <td style={{ lineHeight: "17px" }}>
                                    <b>Document Format :</b> PDF
                                    <br />
                                    <b>Min Size :</b> 30 KB
                                    <br />
                                    <b>Max Size :</b> 300 KB
                                  </td>
                                  <td
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {/* <div className="input-group">
                                           <div
                                             className="input-group-append"
                                             style={{ width: "90%" }}
                                           > */}
                                    <div
                                      style={{ width: "92%" }}
                                      className="file-upload"
                                      data-toggle="tooltip"
                                      data-placement="top"
                                      data-bs-original-title="Document Format : PDF<br>Min Size : 30 KB<br>Max Size : 300KB"
                                    >
                                      <div className="file-select">
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                          className="imagePreview"
                                          id="divyang"
                                        >
                                          {divyangName && (
                                            <a
                                              href={divyangurl || divyangBase64}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              <FaRegFilePdf
                                                style={{ fontSize: "28px" }}
                                                className="text-danger"
                                              />
                                            </a>
                                          )}
                                        </div>
                                        <button className="btn btn-secondary btn-sm file_btn">
                                          Choose File
                                        </button>
                                        <div
                                          className="file-select-name"
                                          id="divyang_name"
                                        >
                                          {divyangName
                                            ? divyangName.length > 60
                                              ? `${divyangName.slice(0, 60)}...`
                                              : divyangName
                                            : "No File Chosen"}
                                        </div>
                                        <input
                                          ref={divyangRef}
                                          type="file"
                                          className="profileimg form-control"
                                          id="upload_divyang"
                                          accept=".PDF"
                                          onChange={(e) =>
                                            handleChange(e, "divyang")
                                          }
                                          style={{
                                            position: "absolute",
                                            opacity: 0,
                                            left: 0,
                                            top: 0,
                                          }}
                                        />
                                      </div>
                                    </div>
                                    {/* </div>
                                           <div className="input-group-prepend"> */}
                                    <button
                                      className="btn btn-secondary remove-btn"
                                      style={{ padding: "8px 12px" }}
                                      type="button"
                                      onClick={() => handleCancel("divyang")}
                                    >
                                      <i className="fa fa-refresh"></i>
                                    </button>
                                    {/* </div>
                                         </div> */}
                                  </td>
                                </tr>
                              )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-12">
                  <div class="card">
                    <div class="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          {/* <h4 style={{ color: "red" }}>
                                   Corection is possible only once. Be carefull before
                                   submitting.
                                 </h4> */}
                        </div>
                        <div
                          style={{ textAlign: "right" }}
                          className="col-md-6"
                        >
                          <button
                            type="button"
                            id="save_btn"
                            className="btn btn-success pull-right"
                            onClick={handleSubmit}
                          >
                            <i className="fa fa-save"></i> Submit
                          </button>

                          {/* <button
                                   type="button"
                                   class="btn btn-secondary  me-2"
                                   id="cancel_btn"
                                 >
                                   Cancel
                                 </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer11 />
          </div>
        </div>
      </div>
      {/* Cropper Modal */}
      {showCropper && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              width: "600px",
              margin: "100px auto",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <Cropper
              src={cropperImage}
              style={{ height: 400, width: "100%" }}
              aspectRatio={3 / 4}
              guides={true}
              ref={cropperRef}
            />
            <div style={{ marginTop: "10px", textAlign: "right" }}>
              <button className="btn btn-primary" onClick={handleCropConfirm}>
                Crop & Validate
              </button>
              <button
                className="btn btn-secondary"
                style={{ marginLeft: "10px" }}
                onClick={handleCropCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Document11;
